import React, { MutableRefObject, useRef, useState } from 'react';
import CalcResult from './CalcResult';
import { FormattedMessage } from 'react-intl';
import messages from '../locales/messages';
import { NumberInput, Button, Checkbox } from 'carbon-components-react';
import ReactGA from 'react-ga4';
const TRACKING_ID = 'G-RHMWQ4WS0C'; // your Measurement ID
import './CalcDisplay.scss';

import {
  MAX_USERS_TIERS,
  SSO_RATES_TIERS,
  MFA_RATES_TIERS,
  AA_RATES_TIERS,
  LMG_RATES_TIERS,
  IA_RATES_TIERS,
} from '../assets/data/tiers.js';

const CalcDisplay = () => {
  ReactGA.initialize(TRACKING_ID);
  // Send pageview with a custom path
  ReactGA.send({
    hitType: 'pageview',
    page: '/landingpage',
    title: 'Landing Page',
  });

  const consumerUsersRef = useRef() as MutableRefObject<HTMLInputElement>;

  const [invalid, setInvalid] = useState(false);

  const DEFAULT_RU_PRICE = 265.2;

  const [isConsumerSSOChecked, setConsumerSSOIsChecked] = useState(false);
  const [isConsumerMFAChecked, setConsumerMFAIsChecked] = useState(false);
  const [isConsumerAAChecked, setConsumerAAIsChecked] = useState(false);
  const [isConsumerLMGChecked, setConsumerLMGIsChecked] = useState(false);
  const [isConsumerIAChecked, setConsumerIAIsChecked] = useState(false);

  const [totalRUs, setTotalRUs] = useState(0);
  const [userCosts, setUserCosts] = useState('');

  const [isShow, setIsShow] = useState(false);

  const onChangeConsumerSSO = () => {
    setConsumerSSOIsChecked(!isConsumerSSOChecked);
  };

  const onChangeConsumerMFA = () => {
    setConsumerMFAIsChecked(!isConsumerMFAChecked);
  };

  const onChangeConsumerAA = () => {
    setConsumerAAIsChecked(!isConsumerAAChecked);
  };

  const onChangeConsumerLMG = () => {
    setConsumerLMGIsChecked(!isConsumerLMGChecked);
  };

  const onChangeConsumerIA = () => {
    setConsumerIAIsChecked(!isConsumerIAChecked);
  };

  const add = (
    e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>,
  ) => {
    e.preventDefault();

    const consumerUsers = consumerUsersRef.current.value;

    if (!consumerUsers && invalid === false) {
      setInvalid(true);
      return;
    }

    const cUsers = parseInt(consumerUsers);
    const totalUsers = cUsers;
    let total_RUs = 0;
    const monthlyCUsers = cUsers;

    if (isConsumerSSOChecked == true) {
      total_RUs += calculate(monthlyCUsers, SSO_RATES_TIERS);
    }

    if (isConsumerMFAChecked == true) {
      total_RUs += calculate(monthlyCUsers, MFA_RATES_TIERS);
    }

    if (isConsumerAAChecked == true) {
      total_RUs += calculate(monthlyCUsers, AA_RATES_TIERS);
    }

    if (isConsumerLMGChecked == true) {
      total_RUs += calculate(monthlyCUsers, LMG_RATES_TIERS);
    }
    if (isConsumerIAChecked == true) {
      total_RUs += calculate(cUsers, IA_RATES_TIERS);
    }

    let userCost = (DEFAULT_RU_PRICE * total_RUs) / totalUsers / 12;
    userCost = isNaN(userCost) ? 0 : userCost;
    const userCostStr = userCost.toFixed(3);

    setTotalRUs(total_RUs);
    setUserCosts(numberWithCommas(userCostStr));
    if (isShow === false) {
      setIsShow(!isShow);
    }
  };

  const handleClick = () => {
    ReactGA.event({
      category: 'calculate',
      action: 'Click',
      label: 'calc',
    });
  };

  const onChangeHandler = (
    e:
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
      | React.ChangeEvent<HTMLInputElement>,
  ) => {
    e.preventDefault();
    setInvalid(false);
  };

  function numberWithCommas(x: string): string {
    return x.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function calculate(users: number, dataset: number[]) {
    handleClick();
    let RUs = 0;

    for (let i = 0; i < MAX_USERS_TIERS.length; i++) {
      if (MAX_USERS_TIERS[i] == 0) continue;
      const currentTier = MAX_USERS_TIERS[i];
      const prevTier = MAX_USERS_TIERS[i - 1];
      const currentTierRate = dataset[i];

      if (users >= currentTier) {
        RUs += (currentTier - prevTier) * currentTierRate;
      } else {
        RUs += (users - prevTier) * currentTierRate;
        RUs = Math.ceil(RUs);
        return RUs;
      }
    }

    return RUs; //This line will only run if total users is greater than the maximum allowed amount of users
  }

  return (
    <div className="calc-display">
      <div
        className={`calc-display__inner ${
          isShow ? 'calc-display__inner--opened' : ''
        }`}
      >
        <h2 className="calc-display__title">
          <FormattedMessage {...messages['appHeadline']} />
        </h2>
        <form onSubmit={add}>
          <div>
            <div className="calc-display__number-input">
              <NumberInput
                allowEmpty={true}
                ref={consumerUsersRef}
                placeholder="eg. 1200000"
                id="consumerUsers"
                invalid={invalid}
                invalidText="Please provide a valid number"
                label={
                  <FormattedMessage
                    {...messages['inputPopulationIndividuals']}
                  />
                }
                value={''}
                min={0}
                size="lg"
                onChange={onChangeHandler}
              />
            </div>
            <fieldset className="cds--checkbox-group">
              <legend className="cds--label">
                <FormattedMessage {...messages['inputUsePopulation']} />
              </legend>
              <Checkbox
                labelText={
                  <FormattedMessage {...messages['inputSingleSignOn']} />
                }
                id="consumerSSO"
                checked={isConsumerSSOChecked}
                onChange={onChangeConsumerSSO}
              />
              <Checkbox
                labelText={<FormattedMessage {...messages['appMultiFactor']} />}
                id="consumerMFA"
                checked={isConsumerMFAChecked}
                onChange={onChangeConsumerMFA}
              />
              <Checkbox
                labelText={
                  <FormattedMessage {...messages['appAdaptiveAccess']} />
                }
                id="consumerAA"
                checked={isConsumerAAChecked}
                onChange={onChangeConsumerAA}
              />
              <Checkbox
                labelText={
                  <FormattedMessage {...messages['appUserLifecycle']} />
                }
                id="consumerLMG"
                checked={isConsumerLMGChecked}
                onChange={onChangeConsumerLMG}
              />
            </fieldset>
          </div>
          <div>
            <Button onClick={add}>Calculate</Button>
          </div>
        </form>
      </div>
      {isShow && <CalcResult totalRUs={totalRUs} userCost={userCosts} />}
    </div>
  );
};

export default CalcDisplay;
